import { useEffect, useRef, useState } from "react";

// Lote 57 (pedido do Robson): "atualizar sozinho" — antes, uma tela deixada aberta (app em
// segundo plano no celular, aba esquecida no desktop) só buscava dado novo quando o usuário
// recarregava a página na mão. Esse hook reaproveita a função `load`/`fetch` que cada tela já
// tem (useEffect(() => { load() }, [])) e dispara ela de novo sozinha em dois gatilhos:
//
// 1) Ao voltar pra tela — troca de app no celular, volta de outra aba do navegador. É o
//    gatilho mais importante: cobre o caso real que o Robson viu (Painel mostrando dado
//    velho depois de ficar um tempo sem abrir).
// 2) A cada `intervalMs` (padrão 15min) enquanto a tela segue aberta e visível, mesmo sem o
//    usuário tocar em nada — útil pra quem deixa o Painel fixo numa tela.
//
// Um "cooldown" de 5s evita disparo duplicado quando focus e visibilitychange acontecem
// juntos (comum em alguns navegadores/celulares). Silencioso de propósito: nenhuma das
// telas que usam isso mostra spinner de carregamento no `load()` normal, então recarregar
// em segundo plano não pisca a tela — só atualiza os números quando a resposta chega.
//
// Lote novo (02/10/2026, pedido do Robson: "não percebi a atualização... isso está
// funcionando?"): o hook sempre funcionou (visibilitychange/focus/15min), mas era
// INVISÍVEL de propósito — sem nenhum jeito de confirmar, na tela, que uma atualização
// aconteceu. Agora devolve também `lastUpdated` (hora da última atualização bem-sucedida,
// já contando a primeira carga inicial) pra cada tela mostrar um texto discreto tipo
// "Atualizado às 14:32" — prova visual de que está funcionando, sem voltar a piscar a
// tela com spinner.
export function useAutoRefresh(refetch, { intervalMs = 15 * 60 * 1000 } = {}) {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const lastRunRef = useRef(Date.now());
  const [lastUpdated, setLastUpdated] = useState(() => new Date());

  useEffect(() => {
    const run = async () => {
      const now = Date.now();
      if (now - lastRunRef.current < 5000) return; // cooldown anti-duplicata
      lastRunRef.current = now;
      try {
        await refetchRef.current?.();
      } finally {
        setLastUpdated(new Date());
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") run();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", run);
    const interval = intervalMs > 0 ? setInterval(run, intervalMs) : null;

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", run);
      if (interval) clearInterval(interval);
    };
  }, [intervalMs]);

  return { lastUpdated };
}
